import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { track } from "../lib/analytics";
import { PageHero } from "../shell/PageHero";
import { CategoryAtAGlance } from "./CategoryAtAGlance";
import {
  CatalogSortSelect,
  DEFAULT_SORT,
  isCatalogSortValue,
  sortTypes,
  type CatalogSortValue
} from "./CatalogSortSelect";
import { EquipmentCard } from "./EquipmentCard";
import { usePublicCategory } from "./api";
import type { TypeSummary } from "./types";

const SPARSE_GRID_THRESHOLD = 4;

// FR-3 — /c/:category_slug. Hero banner with the category's hero
// image, one-paragraph description, then a grid of EquipmentCards.
// Phase 4i refinement layers a controlled client-side sort + result
// count chip + sparse-grid filler card on top of the original layout.
export function CategoryLandingPage(): JSX.Element {
  const { category_slug = "" } = useParams<{ category_slug: string }>();
  const { data, isPending, isError, error } = usePublicCategory(category_slug);

  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort");
  const sort: CatalogSortValue = isCatalogSortValue(sortParam)
    ? sortParam
    : DEFAULT_SORT;

  const sortedTypes = useMemo<TypeSummary[]>(
    () => (data ? sortTypes(data.types, sort) : []),
    [data, sort]
  );

  useEffect(() => {
    if (category_slug) track({ name: "category_viewed", category_slug });
  }, [category_slug]);

  function handleSortChange(next: CatalogSortValue): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === DEFAULT_SORT) {
          params.delete("sort");
        } else {
          params.set("sort", next);
        }
        return params;
      },
      { replace: true }
    );
  }

  if (isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-6 w-1/3" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load this category.
        {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
      </Alert>
    );
  }

  if (!data) return <CategoryNotFound />;

  const totalCount = data.types.length;
  // The audit flagged "2 cards in a 4-col grid reads empty". Anything
  // with fewer than the threshold count gets a brand-red "call us"
  // filler card so the row isn't visually thin. Once a category has
  // 4+ cards the row fills naturally and the filler would crowd it.
  const showFiller = totalCount > 0 && totalCount < SPARSE_GRID_THRESHOLD;

  return (
    <div className="space-y-8 pb-10 md:space-y-10">
      <PageHero
        eyebrow="Catalog"
        title={data.name}
        description={data.description ?? undefined}
        imageUrl={data.hero_image_url ?? undefined}
        size="md"
      />

      <CategoryAtAGlance categoryName={data.name} types={data.types} />

      <nav aria-label="Breadcrumb" className="text-sm text-zinc-600">
        <Link to="/" className="hover:text-brand-red hover:underline">Home</Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <span className="text-brand-black">{data.name}</span>
      </nav>

      {totalCount === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-brand-white p-10 text-center">
          <p className="text-base font-medium text-brand-black">No equipment is published in this category yet.</p>
          <p className="mt-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red hover:text-brand-red-dark hover:underline"
            >
              Browse all categories
              <span aria-hidden="true">→</span>
            </Link>
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-zinc-700" aria-live="polite">
              {`Showing ${totalCount} ${totalCount === 1 ? "match" : "matches"}`}
            </p>
            <CatalogSortSelect value={sort} onChange={handleSortChange} />
          </div>

          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedTypes.map((t) => (
              <li key={t.id}>
                <EquipmentCard type={t} categorySlug={data.slug} />
              </li>
            ))}
            {showFiller ? (
              <li>
                <SparseGridFiller categoryName={data.name} />
              </li>
            ) : null}
          </ul>
        </>
      )}
    </div>
  );
}

function SparseGridFiller({
  categoryName
}: {
  categoryName: string;
}): JSX.Element {
  // Converts a sparse 1-3 card row from "looks empty" to "looks
  // intentional" — the audit specifically called out 2-card 4-col
  // grids reading like the page failed to load.
  return (
    <article
      role="region"
      aria-label="Don't see what you need? Call us"
      className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-brand-red/40 bg-brand-red/5 p-6 text-center"
    >
      <p className="font-serif text-lg font-semibold italic text-brand-black">
        Don&rsquo;t see what you need?
      </p>
      <p className="text-sm text-zinc-700">
        We may carry other {categoryName.toLowerCase()} that aren&rsquo;t
        listed yet, or we can usually source what you need.
      </p>
      <a
        href="tel:+13034354328"
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-red px-3 py-1.5 text-sm font-semibold text-brand-white shadow-sm hover:bg-brand-red-dark"
      >
        Call 303-435-4328
      </a>
    </article>
  );
}

function CategoryNotFound(): JSX.Element {
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-8 text-center">
      <h1 className="text-xl font-semibold text-brand-black">Category not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        The URL may be stale or the category was archived.
      </p>
      <p className="mt-4">
        <Link to="/" className="text-brand-red hover:underline">Browse all categories</Link>
      </p>
    </div>
  );
}
